import React, { FC, useMemo } from 'react';
// models
import type { GroupBase, StylesConfig } from 'react-select';

import Lottie from 'lottie-react';

import styled from 'styled-components';

//themes
import aampeTheme from '../../constants/aampeTheme';
import ANIMATION from '../../constants/animation';
import { FormulaType } from '../../constants/formulaType';
//constants
import ICONS from '../../constants/icons';
import { getCoverMessagePreviewFilterOptions } from '../../constants/messagePreviews';

//hooks
import useHandleQueryParams from '../../hooks/helper/useHandleQueryParams';

import { FormulaCoverSortOptions } from '../../models/formula.model';

//components
import BasicButton from '../buttons/basicButton';
import AampeInputLabel from '../inputs/inputLabel';
import SearchableSelect, {
    SearchableSelectType,
} from '../inputs/searchableSelect';
import AampeTooltip from '../shared/AampeTooltip/AampeTooltip';

const customStyles: StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
> = {
    control: (provided) => ({
        ...provided,
        boxShadow: 'none',
        border: 'none',
        margin: aampeTheme.spacing(0, 1),
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: aampeTheme.spacing(0.5, 0),
        cursor: 'pointer',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: aampeTheme.spacing(0),
        color: aampeTheme.colors.neutral09,
    }),
};
export interface BannerProps {
    title: string;
    description: string;
    showRefresh: boolean;
    showFilter: boolean;
    refreshMessages: () => void;
    formulaType?: FormulaType;
}
interface FilterOption {
    label: string;
    value: FormulaCoverSortOptions;
}
const BannerToolbar: FC<BannerProps> = ({
    title,
    description,
    showRefresh,
    showFilter,
    refreshMessages,
    formulaType = FormulaType.mobilePush,
}) => {
    const filterOptions: Partial<
        Record<FormulaCoverSortOptions, FilterOption>
    > = getCoverMessagePreviewFilterOptions(formulaType);

    const isChromatic = import.meta.env.VITE_CHROMATIC === 'true';
    // const isStorybook = import.meta.env.STORYBOOK === 'true';

    const [queryParams, setQueryParams] = useHandleQueryParams();

    const selected = useMemo(() => {
        const sortByParam = queryParams.sort_by;

        // Check if sortByParam is valid
        if (
            sortByParam &&
            [
                FormulaCoverSortOptions.Header,
                FormulaCoverSortOptions.Body,
                FormulaCoverSortOptions.Message,
                FormulaCoverSortOptions.Random,
            ].includes(sortByParam as FormulaCoverSortOptions)
        ) {
            return filterOptions[sortByParam as FormulaCoverSortOptions];
        }
        return filterOptions[FormulaCoverSortOptions.Random];
    }, [queryParams.sort_by, filterOptions]);

    const handleClick = () => {
        setQueryParams({ current_step: 'alternates' });
    };
    return (
        <BannerContainer>
            <LeftContainer>
                <AnimationContainer>
                    <Lottie
                        style={{
                            height: '80px',
                            width: '80px',
                        }}
                        animationData={ANIMATION.robotForCover}
                        autoplay={!isChromatic}
                        loop={!isChromatic}
                    />
                </AnimationContainer>
                <LeftSubContainer>
                    <Title>{title}</Title>
                    <Description>{description}</Description>
                </LeftSubContainer>
            </LeftContainer>

            <RightContainer>
                {showFilter ? (
                    <RightSubContainer>
                        <SelectContainer>
                            <LabelContainer>
                                <AampeInputLabel label={'Sort by'} />
                            </LabelContainer>
                            <StyledSearchableSelect
                                name={'breakdown'}
                                options={Object.values(filterOptions)}
                                value={selected}
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                onChange={(field, value) => {
                                    if (!Array.isArray(value)) {
                                        setQueryParams({
                                            sort_by: value.value,
                                        });
                                    }
                                }}
                                customStyles={customStyles}
                            />
                        </SelectContainer>
                        {showRefresh && (
                            <AampeTooltip
                                title={'View another 100 alternate messages'}
                            >
                                <div>
                                    <BasicButton
                                        name=""
                                        icon={<ICONS.refreshDouble />}
                                        isPrimary={false}
                                        style={{
                                            minWidth: '40px',
                                            height: '40px',
                                        }}
                                        isCompact
                                        onClick={refreshMessages}
                                    ></BasicButton>
                                </div>
                            </AampeTooltip>
                        )}
                    </RightSubContainer>
                ) : (
                    <LinkWrapper>
                        <StyledLink onClick={handleClick}>
                            Go to alternates table
                        </StyledLink>
                    </LinkWrapper>
                )}
            </RightContainer>
        </BannerContainer>
    );
};

const BannerContainer = styled.div({
    padding: aampeTheme.spacing4(4, 6, 3, 6),
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    zIndex: 12,
    position: 'relative',
    alignItems: 'center',
    background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.90) 85%, rgba(255, 255, 255, 0.76) 100%)',
});

const Title = styled.h1({
    color: aampeTheme.colors.textAccessibleWG02,
    fontSize: aampeTheme.size.font.title,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.tall,
});

const Description = styled.p({
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});

const LeftContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: aampeTheme.spacing4(5),
});

const LeftSubContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
});

const RightContainer = styled.div({
    alignItems: 'center',
});

const RightSubContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: aampeTheme.spacing4(2),
});

const SelectContainer = styled.div({
    display: 'flex',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    alignItems: 'center',
});

const StyledSearchableSelect = styled(SearchableSelect)({
    width: '304px',
});

const LinkWrapper = styled.div({
    marginTop: 'auto',
    flexWrap: 'wrap',
});

const StyledLink = styled.div({
    cursor: 'pointer',
    color: aampeTheme.colors.solidA01,
    fontWeight: aampeTheme.weights.medium,
    '&:hover': {
        color: aampeTheme.colors.solidA02,
    },
});

const LabelContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(2, 2, 0, 2),
    borderRight: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    height: aampeTheme.spacing4(10),
});
const AnimationContainer = styled.div({
    display: 'flex',
});

export default BannerToolbar;
